import type { ButtonPriority, ButtonSize } from '@timelyapp/react';
import { usePrevious } from '@timelyapp/react';
import type { Props as DatePickerProps } from 'components/datepicker/DatePicker';
import DatePicker from 'components/datepicker/DatePicker';
import DatePickerTarget from 'components/datepicker/DatePickerTarget';
import type { DateRange } from 'components/datepicker/types';
import type { RawProps as PopoverProps } from 'components/popovers/Popover';
import Popover from 'components/popovers/Popover';
import React, { useCallback, useEffect, useState } from 'react';
import { sanitizeRange } from './utils';

type PickedPopoverProps = Partial<Pick<PopoverProps, 'fixed' | 'position'>>;
export interface Props extends PickedPopoverProps, DatePickerProps {
  priority?: ButtonPriority;
  /** Optionally provide a dropdown trigger, or leave out for auto-trigger */
  children?: React.ReactElement;
  onClose?: () => void;
  onOpen?: () => void;
  disabled?: boolean;
  isInitiallyOpen?: boolean;
  /** Should the auto generated trigger <Button /> expand full width of container? */
  fullWidth?: boolean;
  /** if year !== current year, show year? (defaults to on) */
  autoPostfixYear?: boolean;
  /** Cosmetic, remove edge styling such as borderRadius (defaults to false) */
  inButtonGroup?: boolean;
  /** Inline style for the auto-generated target button */
  targetStyle?: React.CSSProperties;
  closeOnRangeSet?: boolean;
  startNewRangeOnMount?: boolean;
  size?: ButtonSize;
  isClientReport?: boolean;
}

const DatePickerDropdown: React.FC<Props> = props => {
  const {
    position = 'bottom',
    autoPostfixYear = true,
    initialMode = 'week',
    size = 'small',
    priority,
    closeOnRangeSet,
    onClose,
    dateRange,
    relativeDateRange,
    isClientReport = false,
  } = props;
  const resolvedMode = initialMode === 'custom' ? 'day' : initialMode;
  const [isOpen, setIsOpen] = useState(!!props.isInitiallyOpen);
  const isCustomRangeEnabled = React.useRef<boolean | null>(null);
  const handleToggleDropdown = () => {
    if (props.disabled) {
      return;
    }
    const toBeOpen = !isOpen;
    setIsOpen(toBeOpen);

    if (!toBeOpen && !relativeDateRange) {
      const range = sanitizeRange(
        props.baseDate,
        resolvedMode,
        props.startOfWeek,
        dateRange?.to ? dateRange : undefined
      );
      props.onDateChange(props.baseDate, range);
      props.onClose?.();
    }
    if (toBeOpen) {
      props.onOpen?.();
    }
  };

  const closeDropdown = useCallback(() => {
    onClose?.();
    setIsOpen(false);
  }, [onClose]);

  const handleDateChange = useCallback(
    (date: string, range: DateRange) => {
      props.onDateChange(date, range);
    },
    [props]
  );

  const handleCustomRangeToggle = useCallback((enabled: boolean) => {
    isCustomRangeEnabled.current = enabled;
  }, []);

  const previousDateRangeTo = usePrevious(dateRange?.to);
  const previousDateRangeFrom = usePrevious(dateRange?.from);
  const toggledRangeOff = closeOnRangeSet && isCustomRangeEnabled.current === false;

  useEffect(() => {
    const hasFullDateRange = !!dateRange?.from && !!dateRange?.to;
    // did we turn the customRange off and make a new selection?
    if (toggledRangeOff && hasFullDateRange && previousDateRangeFrom !== dateRange.from) {
      closeDropdown();
    }
    // is the range on or untouched and we made a new selection?
    if (!toggledRangeOff && closeOnRangeSet && !previousDateRangeTo && hasFullDateRange) {
      closeDropdown();
    }
  }, [
    dateRange?.to,
    closeOnRangeSet,
    toggledRangeOff,
    previousDateRangeTo,
    dateRange,
    previousDateRangeFrom,
    onClose,
    closeDropdown,
  ]);

  const target = props.children || (
    <DatePickerTarget
      disabled={props.disabled}
      startOfWeek={props.startOfWeek}
      fullWidth={props.fullWidth}
      baseDate={props.baseDate}
      initialMode={initialMode}
      autoPostfixYear={autoPostfixYear}
      dateRange={props.dateRange}
      inButtonGroup={props.inButtonGroup}
      targetStyle={props.targetStyle}
      size={size}
      priority={priority}
      data-testid="date_picker_dropdown_target"
      relativeDateRange={relativeDateRange}
    />
  );

  return (
    <Popover
      data-testid="date_picker_dropdown"
      fixed={props.fixed}
      position={position}
      target={target}
      onOpen={handleToggleDropdown}
      onClose={handleToggleDropdown}
      isOpen={isOpen}
      zIndex={1200} // Set higher than Navbar's z-index (1001)
      targetWrapperStyle={props.fullWidth ? { flex: 1, display: 'flex' } : undefined}
    >
      <DatePicker
        {...props}
        onCustomRangeEnabledChange={handleCustomRangeToggle}
        initialMode={initialMode}
        onDateChange={handleDateChange}
        isClientReport={isClientReport}
      />
    </Popover>
  );
};

export default DatePickerDropdown;
